/* RESPONSIBLE TEAM: team-app-security */
import type IntlService from 'ember-intl/services/intl';
import type { Role } from 'embercom/components/new-settings/workspace/teammates/scim/types';

export interface DropdownItem {
  text: string;
  value: any;
  onSelectItem: () => void;
  isDisabled?: boolean;
  tooltipText?: string;
}

export interface SelectItem {
  text: string;
  value: any;
  isDisabled?: boolean;
  tooltipText?: string;
}

export interface DropdownGroup {
  items: DropdownItem[];
  heading?: string;
}

export interface SelectGroup {
  items: SelectItem[];
  heading?: string;
}

export function createRoleDropdownList(
  roles: Role[],
  intl: IntlService,
  onSelectRole: (roleId: number | null) => void,
): DropdownGroup[] {
  if (roles.length === 0) {
    return [
      {
        items: [
          {
            text: intl.t('new-settings.workspace.teammates.scim-tab.no-roles-available'),
            value: null,
            onSelectItem: () => {},
          },
        ],
      },
    ];
  }

  let noRoleItem = {
    text: intl.t('settings.teammates.role.no-role'),
    value: 'none',
    onSelectItem: () => onSelectRole(null),
  };

  let roleItems = roles.map((role) => ({
    text: role.name,
    value: role.id,
    onSelectItem: () => onSelectRole(parseInt(role.id, 10)),
  }));

  return [
    {
      items: [noRoleItem, ...roleItems],
    },
  ];
}

export function createRoleSelectGroupList(
  roles: Role[],
  intl: IntlService,
  unassignableRoles?: Record<string, { isDisabled: boolean; tooltipText: string }>,
): SelectGroup[] {
  let roleItems = roles.map((role) => ({
    text: role.name,
    value: role,
    isDisabled: unassignableRoles?.[role.id]?.isDisabled || false,
    tooltipText: unassignableRoles?.[role.id]?.tooltipText,
  }));

  return [
    {
      items: [{ text: intl.t('settings.teammates.role.no-role'), value: null }],
    },
    {
      heading: intl.t('settings.teammates.role.roles'),
      items: roleItems,
    },
  ];
}

export function getRoleNameById(
  roleId: string | number | null | undefined,
  roles: Role[],
  intl: IntlService,
): string {
  if (roles.length === 0) {
    return intl.t('new-settings.workspace.teammates.scim-tab.no-roles-available');
  }

  if (!roleId) {
    return intl.t('settings.teammates.role.no-role');
  }

  let roleIdStr = roleId.toString();
  let role = roles.find((r) => r?.id === roleIdStr);
  return role?.name || intl.t('new-settings.workspace.teammates.scim-tab.unknown-role');
}
